/*!
Home page / Dashboard
*/
import React, {Component} from "react";

// reactstrap components
import {
    Container,
    Row,
    Col,
    Alert,
    Spinner,
    Button,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Input,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Modal,
    ModalBody,
    ModalFooter,
    Progress,
    Badge,
} from "reactstrap";
// layout for this page
import FrontendUI from "layouts/FrontendUI.js";
//classes
import WbSession from "classes/Session.jsx";
// core components
import FrontendHeader from "components/Headers/FrontendHeader.js";
import FieldSet from "components/Features/FieldSet.jsx";
import Field from "components/Features/Field.jsx";

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faEnvelope, faShareAlt } from '@fortawesome/pro-solid-svg-icons';

// Helper Functions
import { cloneDeep, isEmpty } from 'lodash';
import applyConditionalLogic from "classes/ConditionalLogic.jsx";

class ApplicationForm extends Component {
    static contextType = WbSession;
    debug = false;
    state = {
        applicationId: 0,
        newApplication: true,
        applicationCompany: '',
        applicationEmail: '',
        applicationStatus: 'pending', // disables editing when 'submitted', 'approved', 'cancelled'
        
        applicationPassword: null,
        applicationData: {}, // Data as saved on the Server
        applicationState: {}, // Data as entered by the User
        gotApplicationData: false,
        loadingApplication: false,
        submittingApplication: false,
        
        fieldData: {}, // List of fields from Server
        fieldAreaData: {}, // List of fields from Server
        statusTitles: {}, // List of statuses from Server
        gotFieldData: false,
        loadingFields: false, // Show Loading screen when: true
        //Editing & autosave
        fldChanged: false, //show "Are you sure you want to leave?" prompt when unsaved
        allowLeave: false, //allow user to leave after clicking 'yes' on prompt
        savingChanges: false, // Show loading spinner
        saved: false, // Show success banner on save
        submitted: false, // Show success banner on submit
        autosave: false,
        // Tabs
        activeTab: null,
        showInvalidFields: false,
        // Errors
        error: false,
        errorMsg: "",
        // Shareing
        shareVisible: false,
        shareCopied: false,
        // Reset form
        resetForm: false,
        // Resume Application
        maybeResume: false,
    };
    // Autosave system
    lastSave = 0; //timestamp of last save
    savePeriod = 30; //time between autosaves (in seconds)
    asTrigger = null; // trigger autosave 2 seconds after finishing typing
    
    constructor(){
        super();
        const newState = cloneDeep(this.state);
        if( typeof window != "undefined" ){
            const url = new URL(window.location.href);
            if( null != url.searchParams.get('appid') ){
                newState.applicationId = url.searchParams.get('appid');
                newState.applicationPassword = url.searchParams.get('apppwd');
                newState.newApplication = false;
                localStorage.setItem('application_id', newState.applicationId);
                localStorage.setItem('application_password', newState.applicationPassword);
                if(this.debug) console.log("Application resumed from URL: ",newState.applicationId, newState.applicationPassword);
            } else {
                const savedId = localStorage.getItem('application_id') ?? false;
                const savedPassword = localStorage.getItem('application_password') ?? false;
                
                if( savedId && savedPassword ){
                    // Prompt asking user if they want to resume this application
                    newState.maybeResume = true;
                }
            }
        }
        
        if( !newState.newApplication ){
            newState.activeTab = 'business_info';
        }
        
        this.state = newState;
        
        this.noticeRef = React.createRef();
        this.shareRef = React.createRef();
    }
    
    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', this.handleLeavePageNative, {capture: true});
        }
        
        // Use routeChangeStart to prevent navigation inside of the Next app
        // Uses a module variable to bypass the confirm, otherwise we would be in a loop
        //this.router.events.on('routeChangeStart', this.handleLeavePageReact);
        
    }
    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this.handleLeavePageNative, {capture: true});
        }
    }
    
    fetchFields = async () => {
        this.setState({ loadingFields: true, gotFieldData: false });
        
        const data = await this.context.getFields();
        
        if( data.success && typeof data.fields != "undefined" ){
            this.setState({
                loadingFields: false,
                gotFieldData: true,
                fieldData: data.fields ?? {},
                fieldAreaData: data.areas ?? {},
                statusTitles: data.statuses ?? {},
                error: false,
                errorMsg:''
            });
        } else {
            this.setState({
                loadingFields: false,
                gotFieldData: true,
                error: true,
                errorMsg: data.error
            });
        }
    };
    
    fetchApplicationData = async () => {
        const { applicationId, applicationPassword } = this.state;
        
        this.setState({ loadingApplication: true, error: false, gotApplicationData: false });
                
        if(this.debug) console.log('Fetching application: ',applicationId);
        //this.scrollToNotices();
        const serverData = await this.context.get_application( applicationId, applicationPassword );
        
        let newState = {loadingApplication: false, gotApplicationData: true, applicationData: [], error: false, errorMsg: ''};
        if( serverData.success && typeof serverData.app_field_data != "undefined" ){
            newState.applicationData = serverData.app_field_data;
            newState.applicationCompany = serverData.app_data.company;
            newState.applicationEmail = serverData.app_data.primary_email;
            newState.applicationStatus = serverData.app_data.status;
            if( 'submitted' == newState.applicationStatus ){
                newState.activeTab = 'final';
            }
        } else {
            console.warn('Failed to fetch job.', newState);
            let errorMsg = (newState.message || "Failed to fetch job. Please try again later.");
            newState.error = true;
            newState.errorMsg = errorMsg;
        }
        
        // Run check here for autosaved data
        newState.autosave = this._getLatestAutosave();
        
        this.setState(newState);
    };
    
    // Send Company / Email to the server and get Application ID
    startApplication = async () => {
        const { newApplication, applicationCompany, applicationEmail } = this.state;
        if( !newApplication ){
            return false;
        }
        
        this.setState({savingChanges: true, saved: false});
        let newState = {savingChanges: false, saved: false, error: false, errorMsg: ''};
        
        if( '' == applicationCompany ){
            newState.error = true;
            newState.errorMsg = "Please enter your Business Name.";
            this.setState(newState);
        } else if( '' == applicationEmail ){
            newState.error = true;
            newState.errorMsg = "Please enter your Email Address.";
            this.setState(newState);
        }
        
        const serverData = await this.context.create_application( applicationCompany, applicationEmail );
        if(this.debug) console.log('Server response (create application): ', serverData);
        
        if( serverData.success ){
            newState.applicationId = serverData.app_id;
            newState.applicationPassword = serverData.app_pwd;
            newState.newApplication = false;
            newState.saved = true;
            newState.activeTab = 'business_info';
            newState.applicationState = {
                business_name: applicationCompany
            };
            if( typeof window !== 'undefined' ){
                localStorage.setItem('application_id', newState.applicationId);
                localStorage.setItem('application_password', newState.applicationPassword);
                
                // Update URL
                const params = new URLSearchParams(window.location.search);
                params.set('appid', newState.applicationId);
                params.set('apppwd', newState.applicationPassword);
                window.history.replaceState({}, "", decodeURIComponent(`${window.location.pathname}?${params}`));
            }
        } else {
            console.warn('Failed to create application.', serverData);
            let errorMsg = (serverData.error || "Failed to create Application. Please try again later.");
            newState.error = true;
            newState.errorMsg = errorMsg;
        }
        // Show success/error banner
        this.scrollToNotices();
        
        this.setState(newState);
    };
    
    handleLeavePageNative = (e) => {
        const { fldChanged } = this.state;
        if(this.debug) console.log('Leaving page (native)!', e);
        
        if( fldChanged ){
            e.preventDefault();
            return event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        }
    };
    
    // handleLeavePageReact = () => {
    //     const { fldChanged, allowLeave } = this.state;
    //     if(this.debug) console.log('Leaving page (react)!');
    //
    //     if (!fldChanged||allowLeave) return;
    //
    //     if (window.confirm('You have unsaved changes. Are you sure you want to leave?')){
    //         this.setState({allowLeave: true,fldChanged:false});
    //     } else {
    //         this.router.events.emit('routeChangeError');
    //         throw 'routeChange aborted.';
    //     }
    // };
    
    getTabs = () => {
        const { fieldAreaData } = this.state;
        
        let tabs = [
            {
                id: 'registration',
                title: "Registration"
            },
        ];
        
        if( !isEmpty(fieldAreaData) ){
            for (const areaId in fieldAreaData) {
                const areaName = fieldAreaData[areaId].label ?? fieldAreaData[areaId];
                const conditions = fieldAreaData[areaId].conditions ?? [];
                
                if( this.applyConditionalLogic( conditions ) ){
                    tabs.push({
                        id: areaId,
                        title: areaName
                    });
                }
            }
        }
        
        // Add the 'Finalise Application' tab
        tabs.push({
            id: 'final',
            title: 'Finalise Application'
        });
        
        return tabs;
    };
    
    getCurrentTab = () => {
        const {
            activeTab
        } = this.state;
        
        let defaultTab = 'registration';
        let currentTab = activeTab ?? defaultTab;
        if( null == currentTab ){
            currentTab = defaultTab;
        }
        if(this.debug) console.log('activeTab: ',activeTab);
        if(this.debug) console.log('currentTab: ',currentTab);
        
        return currentTab;
    };
    
    previousTab = ( areaId ) => {
        const tabs = this.getTabs();
        let previousTab = areaId;
        let tabFound = false;
        tabs.forEach(tab=>{
            if( tab.id == areaId ){
                tabFound = true;
            }
            if( !tabFound ){
                previousTab = tab.id;
            }
        });
        if(this.debug) console.log('Tab change (previous) to: ',previousTab);
        this.setState({activeTab:previousTab,showInvalidFields:false});
        
        // Scroll to top of page
        this.scrollToNotices();
    };
    
    nextTab = ( areaId ) => {
        const tabs = this.getTabs();
        let nextTab = areaId;
        let tabFound = false;
        let nextTabRecorded = false;
        tabs.forEach(tab=>{
            if( tabFound && !nextTabRecorded ){
                nextTab = tab.id;
                nextTabRecorded = true;
            }
            if( tab.id == areaId ){
                tabFound = true;
            }
        });
        
        // check current tab is completed and valid
        if( this.checkTabDataValid( areaId ) ){
            if(this.debug) console.log('Tab change (next) to: ',nextTab);
            this.setState({activeTab:nextTab,showInvalidFields:false});
            this.saveChanges( false, true );
        } else {
            if(this.debug) console.log('Tab change (next) failed. Data invalid.' );
            this.setState({showInvalidFields:true});
        }
        
        // Scroll to top of page
        this.scrollToNotices();
    };
    
    // Whether the provided tab is a 'next' tab (true) or 'previous' tab (false)
    isFutureTab = ( targetTab ) => {
        const currentTab = this.getCurrentTab();
        let isFuture = false;
        const tabs = this.getTabs();
        let currentTabFound = false;
        let nextTabFound = false;
        tabs.forEach(tab=>{
            if( targetTab == tab.id ){
                nextTabFound = true;
            }
            if( currentTab == tab.id ){
                currentTabFound = true;
                if( !nextTabFound ){
                    isFuture = true;
                }
            }
        });
        
        return isFuture;
    };
    
    checkTabDataValid = (areaId) => {
        const { applicationStatus, newApplication } = this.state;
        let dataReadOnly = false;
        if( 'pending' !== applicationStatus && 'review' !== applicationStatus ){
            dataReadOnly = true;
        }
        
        let currentTabValid = this.state['tabValidity_'+areaId] ?? true;
        
        if( newApplication ){ // Don't allow navigation when application hasn't commenced
            currentTabValid = false;
        }
        
        return (currentTabValid || dataReadOnly);
    };
    
    getTabCompletion = ( tabId ) => {
        const { newApplication, applicationStatus, statusTitles } = this.state;
        let tabCompletion = this.state['tabCompletion_'+tabId] ?? 100;
        let tabColour = 'warning';
        
        if( 'registration' == tabId && newApplication ){
            tabCompletion = 0;
        } else if( 'final' == tabId && 'pending' == applicationStatus ){
            tabCompletion = 0;
            tabColour = statusTitles[applicationStatus]?.colour??'success';
        } else if( 'final' == tabId && 'review' == applicationStatus ){
            tabCompletion = 50;
            tabColour = statusTitles[applicationStatus]?.colour??'success';
        } else if( 'final' == tabId && 'submitted' == applicationStatus ){
            tabCompletion = 95;
            tabColour = statusTitles[applicationStatus]?.colour??'success';
        }
        
        if( newApplication ){ // Set all tabs to '0' when application hasn't started
            tabCompletion = 0;
        }
        
        tabCompletion = Math.max(2, tabCompletion); //don't allow it to show 0
        if( tabCompletion >= 100 ){
            tabColour = 'success';
        }
        
        return { tabCompletion, tabColour };
    };
    
    saveChanges = async ( submit, silent ) => {
        if(this.debug) console.log('Save triggered');
        if( typeof submit == "undefined" ){
            var submit = false;
        }
        
        let showLoadingscreen = true;
        if( submit || silent ){
            showLoadingscreen = false;
        }
        
        this.setState({savingChanges: showLoadingscreen, saved: false, error: false, errorMsg: ''});
        
        // Get data for fields which have been changed
        const toSave = this._getModifiedFields();
        
        if(this.debug) console.log('Saving fields: ', toSave);
        
        this._updateApplication( toSave, submit, silent );
    };
    
    submitApplication = async () => {
        if(this.debug) console.log('Submit triggered');
        this.setState({savingChanges: true, saved: false, submittingApplication:false});
        
        const { applicationId, applicationPassword } = this.state;
        //this.scrollToNotices();
        const serverData = await this.context.submitApplication( applicationId, applicationPassword );
        if(this.debug) console.log('Server response (submit Application): ', serverData);
        
        let newState = {savingChanges: false, saved: false};
        if( serverData.success ){
            newState.fldChanged = false;
            newState.applicationData = serverData.app_field_data;
            newState.submitted = true;
            newState.applicationStatus = 'submitted';
        } else {
            console.warn('Failed to update Application.', serverData);
            let errorMsg = (serverData.error || "Failed to update Application. Please try again later.");
            newState.error = true;
            newState.errorMsg = errorMsg;
        }
        
        this.scrollToNotices();
        
        this.setState(newState);
        
        this._clearAutosaves();
    };
    
    cancelApplication = async () => {
        if(this.debug) console.log('Cancel triggered');
        this.setState({savingChanges: true, saved: false, resetForm:false});
        
        const { applicationId, applicationPassword } = this.state;
        //this.scrollToNotices();
        const serverData = await this.context.cancelApplication( applicationId, applicationPassword );
        if(this.debug) console.log('Server response (cancel Application): ', serverData);
        
        let newState = {savingChanges: false, saved: false};
        // Reset all State data
        newState.applicationId = 0;
        newState.newApplication = true;
        newState.applicationCompany = '';
        newState.applicationEmail = '';
        newState.applicationStatus = 'pending'; // disables editing when 'submitted'; 'approved'; 'cancelled'
        newState.applicationPassword = null;
        newState.applicationData = {}; // Data as saved on the Server
        newState.applicationState = {}; // Data as entered by the User
        newState.gotApplicationData = false;
        newState.loadingApplication = false;
        newState.submittingApplication = false;
        newState.fldChanged = false; //show "Are you sure you want to leave?" prompt when unsaved
        newState.submitted = false; // Show success banner on submit
        newState.autosave = false;
        newState.activeTab = null;
        newState.showInvalidFields = false;
        newState.error = false;
        
        // Clear localStorage
        this._clearAutosaves();
        if( typeof window !== 'undefined' ){
            localStorage.removeItem('application_id');
            localStorage.removeItem('application_password');
            
            // Update URL
            window.history.replaceState({}, "", decodeURIComponent(`${window.location.pathname}`));
        }
        if( !serverData.success ){
            console.warn('Failed to cancel Application.', serverData);
            let errorMsg = (serverData.error || "Failed to cancel Application. Please try again later.");
            newState.error = true;
            newState.errorMsg = errorMsg;
        }
        
        this.scrollToNotices();
        
        this.setState(newState);
    };
    
    scrollToNotices = () => {
        
        this.noticeRef.current.scrollIntoView({behavior: "smooth"});
    };
    
    /**
     * Apply conditional logic to a field to determine if it should display or not
     *
     * @param conds [object] Conditions object, including Action, Multiple, Criteria keys
     *
     * @return bool Whether field is visible (true) or hidden (false)
     */
    applyConditionalLogic = ( conds ) => {
        const { applicationData, applicationState, fieldData } = this.state;
        
        return applyConditionalLogic( conds, applicationData, fieldData, applicationState );
    };
    
    _getModifiedFields = () => {
        const {
            //applicationData,
            applicationState
        } = this.state;
        // Fetch all modified Meta fields
        let toSave = {};
        for (const entry in applicationState) {
            if( typeof entry != "string" ){
                continue;
            }
            toSave[entry] = applicationState[entry];
        }
        
        return toSave;
    };
    
    _pendingAutosave = () => {
        clearTimeout( this.asTrigger );
        this.asTrigger = setTimeout(this._autosave, 2500);
    };
    
    _autosave = () => {
        const { fldChanged, applicationId } = this.state;
        if( !fldChanged || typeof window == 'undefined' ){
            return;
        }
        
        // Check if enough time has elapsed
        const cur_date = new Date();
        const cur_time = cur_date.getTime();
        if(this.debug) console.log('Curtime: ',cur_time,'Last: ',this.lastSave,'Dur: ', cur_time - this.lastSave, 'Refresh', this.savePeriod * 1000);
        if( cur_time - this.lastSave < this.savePeriod * 1000 ){
            return;
        }
        
        // Get data for fields which have been changed
        const toSave = this._getModifiedFields();
        if(this.debug) console.log('Maybe autosave: ',toSave);
        if( isEmpty(toSave) ){
            if(this.debug) console.log('Autosave aborted - no changes to save');
            return; //no changes to save
        }
        this.lastSave = cur_time;
        toSave['timestamp'] = cur_time;
        if(this.debug) console.log('Autosaving: ', toSave);
        // Get version number
        let version = this._getLatestAutosaveVersion();
        if( !version ){
            version = 1;
        }
        version++;
        localStorage.setItem( 'autosave_'+applicationId+'_'+version, JSON.stringify(toSave) );
        localStorage.setItem( 'autosave_'+applicationId+'_version', version );
    };
    
    _getLatestAutosaveVersion = () => {
        const { applicationId } = this.state;
        if( typeof window == 'undefined' ){
            return false;
        }
        
        const latestAutosave = localStorage.getItem('autosave_'+applicationId+'_version');
        
        return latestAutosave ?? false;
    };
    
    _getLatestAutosave = () => {
        const { applicationId } = this.state;
        
        // Determine which version this is
        const version = this._getLatestAutosaveVersion();
        if( !version ){
            return false;
        }
        
        const latestSave = localStorage.getItem('autosave_'+applicationId+'_'+version);
        if(this.debug) console.log('LatestSave: ', latestSave);
        if( null == latestSave || '' == latestSave ){
            return false
        }
        let saveData;
        try{
            saveData = JSON.parse(latestSave);
        } catch(e){
            console.warn('Failed to decode autosave JSON data');
            return false;
        }
        
        // const saveTime = saveData.timestamp ?? 0;
        // if(this.debug) console.log('Comparing autosave time: ',saveTime,' to last update time: ',lmDate.getTime() );
        // if( saveTime < lmDate.getTime() ){
        //     return false;
        // }
        
        return saveData;
    };
    
    _clearAutosaves = () => {
        const { applicationId } = this.state;
        if( typeof window == 'undefined' ){
            return false;
        }
        Object.keys(localStorage).forEach((key) => {
            if( key.startsWith('autosave_'+applicationId) ){
                if(this.debug) console.log('Deleting autosave: ', key);
                localStorage.removeItem(key);
            }
        });
        
        this.setState({autosave: false});
    };
    
    _restoreAutosave = ( saveData ) => {
        const { applicationState } = this.state;
        // Fetch all modified Meta fields
        let newState = {};
        let newApplicationState = cloneDeep(applicationState);
        for (const entry in saveData) {
            if( typeof entry != "string" ){
                continue;
            }
            newApplicationState[entry] = saveData[entry];
        }
        newState['applicationState'] = newApplicationState;
        newState['autosave'] = false;
        newState['fldChanged'] = true;
        
        this.setState( newState );
    };
    
    _updateApplication = async ( toSave, submit, silent ) => {
        const { applicationId, applicationPassword } = this.state;
        
        if(this.debug) console.log('Updating Application with data: ',toSave);
        //this.scrollToNotices();
        const serverData = await this.context.update_application( applicationId, applicationPassword, toSave );
        if(this.debug) console.log('Server response (update Application): ', serverData);
        
        let newState = {savingChanges: false, saved: false};
        if( serverData.success ){
            newState.fldChanged = false;
            newState.applicationData = serverData.app_field_data;
            newState.saved = true;
            if( submit ){
                newState.saved = false;
                newState.submittingApplication = true;
            } else if ( silent ){
                newState.saved = false;
            }
        } else {
            console.warn('Failed to update Application.', serverData);
            let errorMsg = (serverData.error || "Failed to update Application. Please try again later.");
            newState.error = true;
            newState.errorMsg = errorMsg;
        }
        
        this.scrollToNotices();
        
        this.setState(newState);
        
        this._clearAutosaves();
    };
    
    _resumeFromLocalstorage = () => {
        const savedId = localStorage.getItem('application_id') ?? false;
        const savedPassword = localStorage.getItem('application_password') ?? false;
        
        let newState = {};
        newState.applicationId = savedId;
        newState.applicationPassword = savedPassword;
        // Reset all State data
        newState.newApplication = false;
        newState.applicationCompany = '';
        newState.applicationEmail = '';
        newState.applicationStatus = 'pending'; // disables editing when 'submitted'; 'approved'; 'cancelled'
        newState.applicationData = {}; // Data as saved on the Server
        newState.applicationState = {}; // Data as entered by the User
        newState.gotApplicationData = false;
        newState.loadingApplication = false;
        newState.submittingApplication = false;
        newState.fldChanged = false; //show "Are you sure you want to leave?" prompt when unsaved
        newState.submitted = false; // Show success banner on submit
        newState.autosave = false;
        newState.activeTab = null;
        newState.showInvalidFields = false;
        newState.error = false;
        if(this.debug) console.log("Application resumed from Localstorage: ",savedId, savedPassword);
        
        // Update URL
        const params = new URLSearchParams(window.location.search);
        params.set('appid', newState.applicationId);
        params.set('apppwd', newState.applicationPassword);
        window.history.replaceState({}, "", decodeURIComponent(`${window.location.pathname}?${params}`));
        
        this.setState(newState);
    };
    
    render(){
        const {
            applicationId,
            applicationPassword,
            newApplication,
            applicationCompany,
            applicationEmail,
            applicationStatus,
            
            // Application state & server data
            applicationData,
            applicationState,
            gotApplicationData,
            loadingApplication,
            submittingApplication,
            fldChanged,
            fieldData,
            fieldAreaData,
            statusTitles,
            gotFieldData,
            loadingFields,
            savingChanges,
            error,
            errorMsg,
            saved,
            submitted,
            // Tabs
            showInvalidFields,
            // Autosave
            autosave,
            // Sharing
            shareVisible,
            shareCopied,
            // Cancel application
            resetForm,
            // Resume application
            maybeResume,
        } = this.state;
        
        if( !gotFieldData && !loadingFields ){
            this.fetchFields();
        }
        
        if( !newApplication && gotFieldData && !gotApplicationData && !loadingApplication ){
            this.fetchApplicationData();
        }
        
        const currentTab = this.getCurrentTab();
        
        // Make all fields readonly if status is not 'pending' / 'review'
        let dataReadOnly = false;
        if( 'pending' !== applicationStatus && 'review' !== applicationStatus ){
            dataReadOnly = true;
        }
        if(this.debug) console.log('Application State: ', applicationStatus, ' Readonly: ',dataReadOnly);
        if(this.debug) console.log('Autosave wants to be restored: ', autosave);
        
        // Generate share url
        let shareUrl = '';
        if( typeof window != "undefined" ){
            const url = new URL(window.location.href);
            url.searchParams.set('appid', applicationId);
            url.searchParams.set('apppwd', applicationPassword);
            
            shareUrl = url.toString();
        }
        
        const tabs = this.getTabs();
        
        let fieldTabs = [];
        if( !isEmpty(fieldAreaData) ){
            for (const areaId in fieldAreaData) {
                const areaName = fieldAreaData[areaId].label ?? fieldAreaData[areaId];
                const conditions = fieldAreaData[areaId].conditions ?? [];
                const areaTab = (
                    <TabPane tabId={areaId} role="jobTabContent">
                        <Row>
                            <Col xl="12">
                                {(error) ? (
                                    <Alert color="danger">Error: {errorMsg}</Alert>
                                ) : (null)}
                                <FieldSet
                                    name={areaId}
                                    title={areaName}
                                    fields={fieldData}
                                    originalValues={applicationData}
                                    currentValues={(!dataReadOnly)?applicationState:{}}
                                    readOnly={dataReadOnly}
                                    isVisible={(currentTab==areaId)}
                                    showInvalidFields={showInvalidFields}
                                    onChange={(newData, isValid, completion)=>{
                                        if( dataReadOnly )
                                            return;
                                        let newState = {};
                                        newState['applicationState'] = newData;
                                        newState['fldChanged'] = true;
                                        newState['tabValidity_'+areaId] = isValid;
                                        newState['tabCompletion_'+areaId] = completion;
                                        if(this.debug) console.log('Fields updated: ', newData, newState);
                                        this.setState(newState);
                                        this._pendingAutosave();
                                    }}
                                    footer={(
                                        <Row className="justify-content-md-right">
                                            <Col className="col-auto">
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    type="button"
                                                    onClick={ e => {
                                                        e.preventDefault();
                                                        this.previousTab( areaId );
                                                    }}
                                                >
                                                    <FontAwesomeIcon className="inputIcon" icon={faChevronLeft} /> Previous Page
                                                </Button>
                                            </Col>
                                            <Col />
                                            <Col className="col-auto">
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    type="button"
                                                    onClick={ e => {
                                                        e.preventDefault();
                                                        this.nextTab( areaId );
                                                    }}
                                                >
                                                    Next Page <FontAwesomeIcon className="inputIcon" icon={faChevronRight} />
                                                </Button>
                                            </Col>
                                        </Row>
                                    )}
                                />
                            </Col>
                        </Row>
                        
                    </TabPane>
                );
                fieldTabs.push( areaTab );
            }
        }
        
        return (
            <>
                <FrontendHeader />
                <Container className="mt--6" fluid>
                    <div ref={this.noticeRef} className="noticeRefOffset" />
                    <div className="noticeContainer">
                        {(saved) ? (
                            <Alert color="success" onClick={()=>{this.setState({saved:false})}}>Success! Your changes were saved.</Alert>
                        ) : (null)}
                        {(submitted) ? (
                            <Alert color="success" onClick={()=>{this.setState({submitted:false})}}>Your application was successfully submitted.</Alert>
                        ) : (null)}
                        {(error) ? (
                            <Alert color="danger" onClick={()=>{this.setState({error:false})}}>Error: {errorMsg}</Alert>
                        ) : (null)}
                        {(autosave && !dataReadOnly) ? (
                            <Alert color="white" isOpen={(false !== autosave)} style={{ 'box-shadow': '0 4px 6px rgb(50 50 93 / 11%), 0 1px 3px rgb(0 0 0 / 8%)'}}>
                                <span className="alert-text">
                                    An autosave was found on your browser with data that is more recent than the version shown below. Would you like to restore the autosave?
                                    <br />
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this._restoreAutosave( autosave );
                                        }}
                                    >
                                        Restore Autosave
                                    </Button>
                                </span>
                                <button
                                    type="button"
                                    className="close"
                                    data-dismiss="alert"
                                    aria-label="Close"
                                    onClick={() => {this._clearAutosaves()}}
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </Alert>
                        ) : (null)}
                        {(maybeResume&&newApplication)?(
                            <Alert color="white" isOpen={true} style={{ 'box-shadow': '0 4px 6px rgb(50 50 93 / 11%), 0 1px 3px rgb(0 0 0 / 8%)'}}>
                                <span className="alert-text">
                                    An existing application was found on your browser. Would you like to resume this application?
                                    <br />
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this._resumeFromLocalstorage();
                                        }}
                                    >
                                        Resume Application
                                    </Button>
                                </span>
                                <button
                                    type="button"
                                    className="close"
                                    data-dismiss="alert"
                                    aria-label="Close"
                                    onClick={() => {this.setState({maybeResume:false});}}
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </Alert>
                        ):(null)}
                    </div>
                    {/* Tabs */}
                    <div className="nav-wrapper">
                        <Nav className="nav-fill flex-column flex-md-row" pills role="tablist">
                            {tabs.map((fld,i)=>{
                                const { tabCompletion, tabColour } = this.getTabCompletion( fld.id );
                                
                                return (
                                    <NavItem key={i}>
                                        <NavLink
                                            className={
                                                "mb-sm-3 mb-md-0 " +
                                                ( (currentTab===fld.id) ? "active" : "" )
                                            }
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                // if advancing to next tab, check fields & notify of errors
                                                if( !this.isFutureTab(fld.id) || this.checkTabDataValid( currentTab ) ){
                                                    if(this.debug) console.log('Tab change (button) to: ',fld.id);
                                                    this.setState({activeTab:fld.id,showInvalidFields:false});
                                                } else {
                                                    if(this.debug) console.log('Tab change (button) failed. Data invalid.');
                                                    this.setState({showInvalidFields:true});
                                                }
                                            }}
                                        >
                                            {fld.title}
                                            <Progress
                                                barClassName=" progress-xs mb-0"
                                                max="100"
                                                color={tabColour}
                                                value={tabCompletion}
                                            />
                                        </NavLink>
                                    </NavItem>
                                );
                            })}
                        </Nav>
                    </div>
                    {/* Loading Spinner */}
                    {loadingFields ? (
                    	<div className="text-center">
                    		<Spinner color="primary" />
                    		<p>Loading fields...</p>
                    	</div>
                    ) : ( null )}
                    {/* Content */}
                    <TabContent id="jobTabContent" activeTab={currentTab} dataTab={currentTab}>
                        <TabPane tabId="registration" role="jobTabContent">
                            <Card>
                                <CardHeader className="border-0">
                                    <Row className="align-items-center">
                                        <div className="col">
                                            <h2 className="mb-0">Account Registration</h2>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <strong>Business Name</strong><br />
                                        <Field
                                            type='text'
                                            value={applicationCompany}
                                            placeholder='Business Name'
                                            error={false}
                                            fldId='rego_company'
                                            readonly={!newApplication}
                                            onChange={(newValue) => {
                                                if( 0==applicationId && !savingChanges ){
                                                    this.setState({applicationCompany:newValue});
                                                }
                                            }}
                                            className='wb_input_fld'
                                        />
                                        <strong>Email Address</strong><br />
                                        <Field
                                            type='email'
                                            value={applicationEmail}
                                            placeholder='Your Email Address'
                                            error={false}
                                            fldId='rego_company'
                                            readonly={!newApplication}
                                            onChange={(newValue) => {
                                                if( 0==applicationId && !savingChanges ){
                                                    this.setState({applicationEmail:newValue});
                                                }
                                            }}
                                            className='wb_input_fld'
                                        />
                                    </Row>
                                </CardBody>
                                <CardFooter>
                                    <Row className="justify-content-md-right">
                                        {(0!==applicationId) ? (
                                            <Col className="col-auto">
                                                <Button
                                                    className="mb-sm-3 mb-md-3 "
                                                    color="secondary"
                                                    size="sm"
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        this.setState({resetForm:true});
                                                    }}
                                                >
                                                    Cancel Application
                                                </Button>
                                            </Col>
                                        ) : (null)}
                                        <Col />
                                        <Col className="col-auto">
                                            {(0==applicationId) ? (
                                                <Button
                                                    className="mb-sm-3 mb-md-3 "
                                                    color={(0==applicationId&&!savingChanges)?"primary":"secondary"}
                                                    type="button"
                                                    disabled={!newApplication}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if( 0==applicationId && !savingChanges ){
                                                            e.target.blur();
                                                            this.startApplication();
                                                        } else if( 0!==applicationId ){
                                                            // Move to next tab
                                                            this.nextTab( 'registration',  );
                                                        }
                                                    }}
                                                >
                                                    Start Application <FontAwesomeIcon className="inputIcon" icon={faChevronRight} />
                                                </Button>
                                            ) : (
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    type="button"
                                                    onClick={ e => {
                                                        e.preventDefault();
                                                        this.nextTab( 'registration' );
                                                    }}
                                                >
                                                    Next Page <FontAwesomeIcon className="inputIcon" icon={faChevronRight} />
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                </CardFooter>
                            </Card>
                        </TabPane>
                        {fieldTabs}
                        <TabPane tabId="final" role="jobTabContent">
                            <Card>
                                <CardHeader className="border-0">
                                    <Row className="align-items-center">
                                        <div className="col">
                                            <h3 className="mb-0">Finalise Application</h3>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <strong>Application status: </strong><Badge color={statusTitles[applicationStatus]?.colour??'primary'}>{statusTitles[applicationStatus]?.title??applicationStatus}</Badge>
                                    <p className="status_desc">{statusTitles[applicationStatus]?.description??''}</p>
                                    
                                </CardBody>
                                
                                <CardFooter>
                                    <Row className="justify-content-md-right">
                                        <Col className="col-auto">
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                type="button"
                                                onClick={ e => {
                                                    e.preventDefault();
                                                    this.previousTab( 'final' );
                                                }}
                                            >
                                                <FontAwesomeIcon className="inputIcon" icon={faChevronLeft} /> Previous Page
                                            </Button>
                                        </Col>
                                        <Col />
                                        {(!dataReadOnly) ? (
                                            <Col className="col-auto">
                                                <Button
                                                    className="mb-sm-3 mb-md-3 "
                                                    color={(!savingChanges)?"primary":"secondary"}
                                                    type="button"
                                                    disabled={savingChanges}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.target.blur();
                                                        
                                                        //TODO: check status of all fields & prompt errors
                                                        this.saveChanges( true, false );
                                                    }}
                                                >
                                                    Submit Application <FontAwesomeIcon className="inputIcon" icon={faEnvelope} />
                                                </Button>
                                            </Col>
                                        ) : (null)}
                                    </Row>
                                </CardFooter>
                            </Card>
                        </TabPane>
                    </TabContent>
                    {(0!=applicationId && !dataReadOnly) ? (
                        <Row className="align-items-right">
                            <Col xl="12" className="text-align-right">
                                <Button
                                    className={"mb-sm-3 mb-md-3 "}
                                    color={(fldChanged)?"primary":"secondary"}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if( fldChanged && !savingChanges ){
                                            e.target.blur();
                                            this.saveChanges( false, false );
                                        }
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </Col>
                        </Row>
                    ) : null}
                    {(!newApplication) ? (
                        <Row className="align-items-right">
                            <Col className="col-auto">
                                <Button
                                    className={"mb-sm-3 mb-md-3 "}
                                    color={"secondary"}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setState({shareVisible:!shareVisible});
                                        // this.shareRef.current.select();
                                    }}
                                >
                                    <FontAwesomeIcon className="inputIcon" icon={faShareAlt} />
                                </Button>
                            </Col>
                            {(shareVisible) ? (
                                <Col>
                                    <Input
                                        type='url'
                                        ref={this.shareRef}
                                        value={shareUrl}
                                        id='share_url'
                                        readonly={true}
                                        onChange={null}
                                        className='wb_input_fld'
                                        onClick={e=>{
                                            console.log('Share url click');
                                            e.target.select();
                                            
                                            navigator.clipboard.writeText(e.target.value);
                                            this.setState({shareCopied:true});
                                            setTimeout(()=>{
                                                this.setState({shareCopied:false});
                                            },1500);
                                        }}
                                    />
                                    {(shareCopied) ? (
                                        <Alert color="success">Copied to clipboard.</Alert>
                                    ) : (null)}
                                </Col>
                            ) : null}
                        </Row>
                    ) : null}
                </Container>
                
                <Modal isOpen={savingChanges}>
                    <div className=" modal-header">
                        <h5 className=" modal-title" id="loadingModalLabel">
                            Saving Changes...
                        </h5>
                    </div>
                    <ModalBody>
                        <div className="text-center">
                            <Spinner color="primary" />
                            <p></p>
                        </div>
                    </ModalBody>
                </Modal>
                
                <Modal toggle={() => this.setState({submittingApplication:false})} isOpen={submittingApplication}>
                    <div className=" modal-header">
                        <h5 className=" modal-title" id="statusChangeModalLabel">
                            Submit Application?
                        </h5>
                        <button
                            aria-label="Close"
                            className=" close"
                            type="button"
                            onClick={() => this.setState({submittingApplication:false})}
                        >
                            <span aria-hidden={true}>×</span>
                        </button>
                    </div>
                    <ModalBody>Are you sure you are ready to submit your Application? You will not be able to edit your Application after submitting.</ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            type="button"
                            onClick={() => this.setState({submittingApplication:false})}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={() => this.submitApplication()}
                        >
                            Submit Application
                        </Button>
                    </ModalFooter>
                </Modal>
                
                <Modal toggle={() => this.setState({resetForm:false})} isOpen={resetForm}>
                    <div className=" modal-header">
                        <h5 className=" modal-title" id="statusChangeModalLabel">
                            Cancel Application?
                        </h5>
                        <button
                            aria-label="Close"
                            className=" close"
                            type="button"
                            onClick={() => this.setState({resetForm:false})}
                        >
                            <span aria-hidden={true}>×</span>
                        </button>
                    </div>
                    <ModalBody>Are you sure you want to cancel your Application? All application data will be lost.</ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            type="button"
                            onClick={() => this.setState({resetForm:false})}
                        >
                            Resume
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            onClick={() => this.cancelApplication()}
                        >
                            Cancel Application
                        </Button>
                    </ModalFooter>
                </Modal>
                
                
            </>
        );
    }
}

ApplicationForm.layout = FrontendUI;

export default ApplicationForm;
